/**
 * Social handles on user profiles.
 *
 * The database stores the BARE HANDLE ("luckyshotsgg"), never a full URL — see
 * the users_social_handles migration. Profile URLs are derived here so there is
 * exactly one place that knows each platform's link shape.
 */

export type SocialPlatform = 'twitch' | 'youtube' | 'x' | 'tiktok' | 'instagram'

export const SOCIAL_PLATFORMS: readonly SocialPlatform[] = [
  'twitch',
  'youtube',
  'x',
  'tiktok',
  'instagram',
] as const

/** Column on `users` backing each platform. */
export const SOCIAL_FIELD: Record<SocialPlatform, string> = {
  twitch: 'twitch_handle',
  youtube: 'youtube_handle',
  x: 'x_handle',
  tiktok: 'tiktok_handle',
  instagram: 'instagram_handle',
}

export const SOCIAL_LABEL: Record<SocialPlatform, string> = {
  twitch: 'Twitch',
  youtube: 'YouTube',
  x: 'X',
  tiktok: 'TikTok',
  instagram: 'Instagram',
}

/**
 * Mirror of the DB's normalize_social_handle(): strip a pasted URL to its last
 * path segment, drop a leading @, and collapse blank to null. Kept in sync so
 * the client can show the stored value before the round-trip.
 */
export function normalizeHandle(raw: string | null | undefined): string | null {
  let v = (raw ?? '').trim()
  if (!v) return null

  if (/^(https?:\/\/|www\.)/i.test(v) || /^[a-z0-9.-]+\.(tv|com|be|gg)\//i.test(v)) {
    v = v
      .replace(/^https?:\/\//i, '')
      .replace(/\?.*$/, '')
      .replace(/#.*$/, '')
      .replace(/\/+$/, '')
      .replace(/^.*\//, '')
  }

  v = v.trim().replace(/^@+/, '').trim()
  return v || null
}

/** Public profile URL for a bare handle, or null when there is no handle. */
export function socialUrl(
  platform: SocialPlatform,
  handle: string | null | undefined
): string | null {
  const h = normalizeHandle(handle)
  if (!h) return null
  const slug = encodeURIComponent(h)
  switch (platform) {
    case 'twitch':
      return `https://twitch.tv/${slug}`
    case 'youtube':
      return `https://youtube.com/@${slug}`
    case 'x':
      return `https://x.com/${slug}`
    case 'tiktok':
      return `https://tiktok.com/@${slug}`
    case 'instagram':
      return `https://instagram.com/${slug}`
  }
}

/** How a handle is shown in the UI — @-prefixed everywhere except Twitch. */
export function socialDisplay(
  platform: SocialPlatform,
  handle: string | null | undefined
): string | null {
  const h = normalizeHandle(handle)
  if (!h) return null
  return platform === 'twitch' ? h : `@${h}`
}

type WithSocials = {
  twitch_handle?: string | null
  youtube_handle?: string | null
  x_handle?: string | null
  tiktok_handle?: string | null
  instagram_handle?: string | null
}

/** Every linked account on a profile, in display order. Empty when none. */
export function userSocials(
  user: WithSocials | null | undefined
): { platform: SocialPlatform; handle: string; url: string; label: string }[] {
  if (!user) return []
  const out: { platform: SocialPlatform; handle: string; url: string; label: string }[] = []
  for (const platform of SOCIAL_PLATFORMS) {
    const raw = user[SOCIAL_FIELD[platform] as keyof WithSocials]
    const handle = normalizeHandle(raw)
    const url = socialUrl(platform, handle)
    if (handle && url) {
      out.push({ platform, handle, url, label: SOCIAL_LABEL[platform] })
    }
  }
  return out
}
